import { from } from "rxjs";
import { gateway, blobProblem, saveBlob } from "./signService";

// signature-gateway's signer management (the gateway's signer-management.md,
// signer-certificates.md; its Swagger is the source of truth): a workspace's
// signing tools ("signers" in the API), the catalogue they are made from, their
// verification credentials and, for the cloud kinds, their key's certificate.
// The same client as signing (signService): bearer, Accept-Language, one retry
// after a 401. Workspaces and signers by their UUIDs only.
const ws = (workspaceId) => `api/remotesign/workspace/${workspaceId}/signer`;
const one = (workspaceId, uuid) => `${ws(workspaceId)}/${uuid}`;

// the cloud kinds: a key of their own at remote-signer, certified
export const CLOUD_KINDS = ["CLOUD_REMOTE_CA", "CLOUD_REMOTE_SEAL_SIG"];
export const isCloudSigner = (s) => !!s && (CLOUD_KINDS.includes(s.kind) || CLOUD_KINDS.includes(s.type));

// save an authorized download under the given name (the gateway's own
// Content-Disposition name, which a cross-origin page may not read)
const download = (path, name) =>
  gateway
    .get(path, { responseType: "blob" })
    .then((res) => saveBlob(res.data, name))
    .catch(blobProblem);

class SignerManagementService {
  // { kinds[], authTypes[], engines[], certifications[] }, labels in the
  // app's language; no-store: fetched whenever a dialog opens
  catalogue = () => from(gateway.get("api/remotesign/signer-catalogue"));

  // { workspaceId, owner, creatableKinds[], unavailableKinds[{kind, reason,
  // reasonLabel}], signers[] (ManagedSigner) }
  list = (workspaceId) => from(gateway.get(ws(workspaceId)));

  // what the workspace's cloud signing tools may be certified by, and the
  // organization's identity their certificates name -> { standing
  // (NO_ORGANIZATION | NOT_VETTED | VETTED), subjectFixed, organization
  // {legalName, country, identifiers[]}, options[{kind, certification,
  // certificationLabel, available, unavailableReason(Label), commonName}] };
  // the owner's, read whenever a certification step opens (never cached)
  certificationOptions = (workspaceId) =>
    from(gateway.get(`api/remotesign/workspace/${workspaceId}/signer-certification-options`));

  get = (workspaceId, uuid) => from(gateway.get(one(workspaceId, uuid)));

  // { kind, name, authTypes?, verificationEngine?, certification?, subject?,
  // validityYears? } -> 201 (or 200: the workspace's one-per-workspace signer)
  create = (workspaceId, body) => from(gateway.post(ws(workspaceId), body));

  rename = (workspaceId, uuid, name) => from(gateway.patch(one(workspaceId, uuid), { name }));

  // { pdf, image, video }: the verification evidence the next signings keep
  // (at least one on)
  setEvidence = (workspaceId, uuid, verificationEvidence) =>
    from(gateway.patch(one(workspaceId, uuid), { verificationEvidence }));

  deactivate = (workspaceId, uuid) => from(gateway.post(`${one(workspaceId, uuid)}/deactivate`));

  activate = (workspaceId, uuid) => from(gateway.post(`${one(workspaceId, uuid)}/activate`));

  // 204; 409 SIGNER_IN_USE for one that was ever usable or used
  remove = (workspaceId, uuid) => from(gateway.delete(one(workspaceId, uuid)));

  // { companyKey, hmacSecret, configId? }: validated at the engine first,
  // never shown again - never log them
  setCredentials = (workspaceId, uuid, credentials) =>
    from(gateway.put(`${one(workspaceId, uuid)}/verification-credentials`, credentials));

  validateCredentials = (workspaceId, uuid) =>
    from(gateway.post(`${one(workspaceId, uuid)}/verification-credentials/validate`));

  // -> 202; without a body the newest request is repeated; a signer without
  // any request needs { certification, subject, validityYears? }
  requestCertificate = (workspaceId, uuid, body) =>
    from(gateway.post(`${one(workspaceId, uuid)}/certificate-request`, body || undefined));

  downloadRequest = (workspaceId, uuid) => from(download(`${one(workspaceId, uuid)}/certificate-request`, `${uuid}.csr`));

  downloadCertificate = (workspaceId, uuid) => from(download(`${one(workspaceId, uuid)}/certificate`, `${uuid}.pem`));

  // the PKI's file itself (application/x-pem-file), or pasted PEM ({ pem })
  uploadCertificate = (workspaceId, uuid, { file, pem }) =>
    from(
      file
        ? gateway.put(`${one(workspaceId, uuid)}/certificate`, file, {
            headers: { "Content-Type": "application/x-pem-file" },
          })
        : gateway.put(`${one(workspaceId, uuid)}/certificate`, { pem })
    );
}

export default new SignerManagementService();
